// === Slide Types ===

export type SlideType = 'title' | 'verse' | 'song' | 'blank' | 'image' | 'video';

// === Canvas Element Types ===

export type SlideElementType = 'text' | 'image' | 'shape';

export interface BaseElement {
  id: string;
  type: SlideElementType;
  /** px in 1920×1080 space */
  x: number;
  /** px in 1920×1080 space */
  y: number;
  /** px in 1920×1080 space */
  width: number;
  /** px in 1920×1080 space */
  height: number;
  /** degrees 0–360 */
  rotation: number;
  /** 0–1 */
  opacity: number;
  zIndex: number;
  locked?: boolean;
}

export interface TextElement extends BaseElement {
  type: 'text';
  content: string;
  fontFamily: string;
  /** absolute px in 1920×1080 space */
  fontSize: number;
  fontWeight: number;
  fontStyle: 'normal' | 'italic';
  fontColor: string;
  textAlign: 'left' | 'center' | 'right';
  textDecoration: 'none' | 'underline' | 'line-through' | 'underline line-through';
  lineHeight: number;
  letterSpacing: number;
  textTransform: 'none' | 'uppercase' | 'lowercase';
  textEffect?: TextEffect | null;
  /** highlight color behind text block */
  bgColor?: string;
  /** px padding around highlight */
  bgPadding?: number;
  /** when true, height grows with content; width is fixed */
  autoHeight?: boolean;
}

export interface ImageFilters {
  brightness?: number;  // 0–200, default 100
  contrast?: number;    // 0–200, default 100
  saturation?: number;  // 0–200, default 100
  blur?: number;        // px, default 0
  grayscale?: number;   // 0–100, default 0
}

export interface ImageElement extends BaseElement {
  type: 'image';
  src: string;
  fit: 'cover' | 'contain' | 'stretch' | 'none';
  filters?: ImageFilters;
  cornerRadius?: number;
  flipH?: boolean;
  flipV?: boolean;
}

export type ShapeKind = 'rect' | 'ellipse' | 'triangle' | 'line' | 'diamond';

export interface GradientStop {
  /** 0–100 */
  offset: number;
  color: string;
}

export interface GradientFill {
  type: 'linear' | 'radial';
  angle?: number;
  stops: GradientStop[];
}

export interface ShapeElement extends BaseElement {
  type: 'shape';
  shape: ShapeKind;
  fill: string;
  fillGradient?: GradientFill;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
}

export type SlideElement = TextElement | ImageElement | ShapeElement;

// ============================================================================

export interface SlideContent {
  mainText: string;
  subText?: string;
  bibleRef?: {
    book: string;
    chapter: number;
    verse: number;
    verseTo?: number;
  };
  /** Canvas Designer mode: when present, renderer uses elements instead of mainText/subText */
  elements?: SlideElement[];
  /** Video slide: direct URL to video file. Receiver plays full-bleed, looped, muted. */
  videoUrl?: string;
  videoAutoplay?: boolean;
}

/**
 * Transition presets supported by the receiver.
 * - cut:       instant. dur=0, no animation.
 * - fade:      opacity fade.
 * - slide-up:  text translateY entry (image fades).
 * - zoom:      image scale-from-1.05 entry (Ken Burns intro).
 *
 * Legacy values 'none' (= cut) and 'slide' (= slide-up) are accepted for
 * backwards compatibility; receiver normalises them via resolveTransition().
 */
export type TransitionKind = 'cut' | 'fade' | 'slide-up' | 'zoom';
export type StyleTransition = TransitionKind | 'none' | 'slide';

// === Background patterns (CSS-rendered, animated optional) ===
//
// Mutually exclusive with bgImage on a slide. Receiver renders into a dedicated
// <div id="bg-pattern"> layer between bgColor and bgImage. Sender previews
// (ScaledSlide, ReceiverPreview iframe) use the same patternToCss helper.

export type BgPatternPreset =
  | 'linear-gradient'
  | 'radial-vignette'
  | 'mesh-soft'
  | 'dots-pattern'
  | 'aurora'
  | 'pulse-glow'
  | 'slow-shift'
  | 'noise-grain'
  | 'mesh-flow'
  | 'bokeh-drift'
  | 'prism-conic';

export interface BgPattern {
  preset: BgPatternPreset;
  /** 2-4 colors depending on preset. See BG_PATTERN_PRESETS for required count. */
  colors: string[];
  /** 1 (slowest) — 10 (fastest). Default 5. Ignored for static presets. */
  speed?: number;
  /** Direction in degrees (linear-gradient / slow-shift). Default 135 / 90. */
  angle?: number;
}

export interface BgPatternMeta {
  /** Display name for UI. */
  label: string;
  /** Required color count (UI shows that many color pickers). */
  colorCount: 2 | 3 | 4;
  /** Whether the preset is animated (UI shows speed slider). */
  animated: boolean;
  /** Whether direction angle applies (UI shows angle slider). */
  hasAngle: boolean;
  /** Default values for newly-created instances. */
  defaults: BgPattern;
}

export const BG_PATTERN_PRESETS: Record<BgPatternPreset, BgPatternMeta> = {
  'linear-gradient': {
    label: 'Degradado lineal', colorCount: 2, animated: false, hasAngle: true,
    defaults: { preset: 'linear-gradient', colors: ['#1e3a8a', '#7c3aed'], angle: 135 },
  },
  'radial-vignette': {
    label: 'Viñeta radial', colorCount: 2, animated: false, hasAngle: false,
    defaults: { preset: 'radial-vignette', colors: ['#1e293b', '#020617'] },
  },
  'mesh-soft': {
    label: 'Mesh suave', colorCount: 4, animated: false, hasAngle: false,
    defaults: { preset: 'mesh-soft', colors: ['#1e3a8a', '#7c3aed', '#db2777', '#0f172a'] },
  },
  'dots-pattern': {
    label: 'Puntos', colorCount: 2, animated: false, hasAngle: false,
    defaults: { preset: 'dots-pattern', colors: ['#0f172a', '#475569'] },
  },
  aurora: {
    label: 'Aurora', colorCount: 4, animated: true, hasAngle: false,
    defaults: { preset: 'aurora', colors: ['#0f172a', '#1e3a8a', '#7c3aed', '#db2777'], speed: 5 },
  },
  'pulse-glow': {
    label: 'Pulso', colorCount: 2, animated: true, hasAngle: false,
    defaults: { preset: 'pulse-glow', colors: ['#1e3a8a', '#020617'], speed: 5 },
  },
  'slow-shift': {
    label: 'Onda lenta', colorCount: 2, animated: true, hasAngle: true,
    defaults: { preset: 'slow-shift', colors: ['#7c3aed', '#db2777'], speed: 5, angle: 90 },
  },
  'noise-grain': {
    label: 'Grano', colorCount: 2, animated: false, hasAngle: false,
    defaults: { preset: 'noise-grain', colors: ['#1e293b', '#475569'] },
  },
  'mesh-flow': {
    label: 'Mesh flujo', colorCount: 4, animated: true, hasAngle: false,
    defaults: { preset: 'mesh-flow', colors: ['#1e3a8a', '#7c3aed', '#db2777', '#0f172a'], speed: 5 },
  },
  'bokeh-drift': {
    label: 'Bokeh', colorCount: 3, animated: true, hasAngle: false,
    defaults: { preset: 'bokeh-drift', colors: ['#7c3aed', '#db2777', '#0f172a'], speed: 5 },
  },
  'prism-conic': {
    label: 'Prisma', colorCount: 3, animated: true, hasAngle: false,
    defaults: { preset: 'prism-conic', colors: ['#1e3a8a', '#7c3aed', '#db2777'], speed: 5 },
  },
};

export interface PatternCss {
  /** CSS `background` value (with optional layered images). */
  background: string;
  /** CSS `background-size`. Required for tiled patterns. */
  backgroundSize?: string;
  /** CSS `animation` value. Empty for static presets. */
  animation?: string;
  /** Matching `transform-origin` for transform-based animations. */
  transformOrigin?: string;
}

/** Render a BgPattern to inline-style CSS. Caller applies via `style.background`,
 *  `style.backgroundSize`, `style.animation`. Animation `@keyframes` are declared
 *  globally in receiver/index.html and apps/web (must match the names used here). */
export function patternToCss(p: BgPattern): PatternCss {
  const speed = Math.min(10, Math.max(1, p.speed ?? 5));
  const colors = p.colors;
  const c0 = colors[0] ?? '#000', c1 = colors[1] ?? '#000';
  const c2 = colors[2] ?? c1, c3 = colors[3] ?? c0;

  switch (p.preset) {
    case 'linear-gradient': {
      const angle = p.angle ?? 135;
      return { background: `linear-gradient(${angle}deg, ${c0}, ${c1})` };
    }
    case 'radial-vignette':
      return { background: `radial-gradient(ellipse at center, ${c0} 0%, ${c1} 100%)` };
    case 'mesh-soft':
      return {
        background:
          `radial-gradient(at 20% 20%, ${c0} 0%, transparent 50%),` +
          `radial-gradient(at 80% 25%, ${c1} 0%, transparent 50%),` +
          `radial-gradient(at 30% 80%, ${c2} 0%, transparent 50%),` +
          `${c3}`,
      };
    case 'dots-pattern':
      return {
        background: `radial-gradient(${c1} 1.4px, transparent 1.6px), ${c0}`,
        backgroundSize: '24px 24px',
      };
    case 'aurora': {
      // speed 1 → 22s, speed 10 → 12s
      const dur = `${22 - speed}s`;
      return {
        background: `linear-gradient(120deg, ${c0}, ${c1}, ${c2}, ${c3})`,
        backgroundSize: '400% 400%',
        animation: `proclaim-aurora ${dur} ease-in-out infinite`,
      };
    }
    case 'pulse-glow': {
      // speed 1 → 13s, speed 10 → 4s
      const dur = `${14 - speed}s`;
      return {
        background: `radial-gradient(circle at center, ${c0} 0%, ${c1} 70%)`,
        animation: `proclaim-pulse ${dur} ease-in-out infinite alternate`,
        transformOrigin: 'center',
      };
    }
    case 'slow-shift': {
      const angle = p.angle ?? 90;
      // speed 1 → 33s, speed 10 → 15s
      const dur = `${35 - speed * 2}s`;
      return {
        background: `linear-gradient(${angle}deg, ${c0}, ${c1}, ${c0})`,
        backgroundSize: '300% 300%',
        animation: `proclaim-slow-shift ${dur} linear infinite`,
      };
    }
    case 'noise-grain': {
      // SVG turbulence baked into a data URL — keeps the receiver self-contained
      // (no network fetch). Layered on top of a soft linear gradient base.
      const noise = encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240">' +
        '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="1"/></filter>' +
        '<rect width="100%" height="100%" filter="url(#n)" opacity="0.45"/></svg>'
      );
      return {
        background: `url("data:image/svg+xml,${noise}"), linear-gradient(135deg, ${c0}, ${c1})`,
      };
    }
    case 'mesh-flow': {
      // mesh-soft animado: cada radial-gradient drift a su propia velocidad/dirección
      // via background-position multi-layer. background-size 200% 200% deja espacio
      // para que cada gradiente se desplace dentro de la layer sin recortar.
      // speed 1 → 40s, speed 10 → 16s
      const dur = `${44 - speed * 2.5}s`;
      return {
        background:
          `radial-gradient(circle at 20% 20%, ${c0} 0%, transparent 50%),` +
          `radial-gradient(circle at 80% 25%, ${c1} 0%, transparent 50%),` +
          `radial-gradient(circle at 30% 80%, ${c2} 0%, transparent 50%),` +
          `${c3}`,
        backgroundSize: '200% 200%, 200% 200%, 200% 200%, 100% 100%',
        animation: `proclaim-mesh-flow ${dur} ease-in-out infinite`,
      };
    }
    case 'bokeh-drift': {
      // 4 círculos radial flotando — drift independiente por capa.
      // speed 1 → 38s, speed 10 → 14s
      const dur = `${42 - speed * 2.8}s`;
      return {
        background:
          `radial-gradient(circle at 15% 30%, ${c0} 0%, transparent 25%),` +
          `radial-gradient(circle at 75% 60%, ${c1} 0%, transparent 28%),` +
          `radial-gradient(circle at 45% 85%, ${c0} 0%, transparent 22%),` +
          `radial-gradient(circle at 85% 15%, ${c1} 0%, transparent 26%),` +
          `${c2}`,
        backgroundSize: '180% 180%, 180% 180%, 180% 180%, 180% 180%, 100% 100%',
        animation: `proclaim-bokeh-drift ${dur} ease-in-out infinite`,
      };
    }
    case 'prism-conic': {
      // conic-gradient con hue-rotate — colores ciclan suave sin geometría rotando
      // (evita overflow problems con transform: rotate). Cheap GPU.
      // speed 1 → 60s, speed 10 → 20s
      const dur = `${64 - speed * 4.5}s`;
      return {
        background: `conic-gradient(from 0deg at 50% 50%, ${c0}, ${c1}, ${c2}, ${c0})`,
        animation: `proclaim-hue-cycle ${dur} linear infinite`,
      };
    }
  }
}

// === Text effects ============================================================

export type TextEffectPreset = 'shadow' | 'glow' | 'outline' | 'neon' | 'emboss';

export interface TextEffect {
  preset: TextEffectPreset;
  /** Hex color from <input type="color">. */
  color: string;
  /** 1–10 strength multiplier. */
  intensity: number;
}

export const TEXT_EFFECT_PRESETS: Record<TextEffectPreset, { label: string; defaultColor: string }> = {
  shadow:  { label: 'Sombra',   defaultColor: '#000000' },
  glow:    { label: 'Brillo',   defaultColor: '#ffffff' },
  outline: { label: 'Contorno', defaultColor: '#000000' },
  neon:    { label: 'Neón',     defaultColor: '#00e5ff' },
  emboss:  { label: 'Relieve',  defaultColor: '#000000' },
};

function _hexRgba(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a.toFixed(2)})`;
}

/** Returns a CSS `text-shadow` value for the given effect, or `'none'`. */
export function textEffectToCss(effect: TextEffect | null | undefined): string {
  if (!effect) return 'none';
  const { preset, color, intensity } = effect;
  const i = Math.max(1, Math.min(10, intensity));
  const c = color.startsWith('#') ? color : '#ffffff';

  switch (preset) {
    case 'shadow': {
      const shadow = _hexRgba(c, 0.25 + i * 0.07);
      return `${(i * 0.3).toFixed(1)}px ${(i * 0.5).toFixed(1)}px ${(i * 2.5).toFixed(1)}px ${shadow}`;
    }
    case 'glow': {
      return `0 0 ${i * 3}px ${c}, 0 0 ${i * 6}px ${c}, 0 0 ${i * 12}px ${c}`;
    }
    case 'outline': {
      const t = Math.max(1, Math.round(i * 0.4));
      const layers: string[] = [];
      for (let x = -t; x <= t; x++)
        for (let y = -t; y <= t; y++)
          if (x !== 0 || y !== 0) layers.push(`${x}px ${y}px 0 ${c}`);
      return layers.join(', ');
    }
    case 'neon': {
      return [
        `0 0 ${i * 2}px #fff`,
        `0 0 ${i * 5}px ${c}`,
        `0 0 ${i * 10}px ${c}`,
        `0 0 ${i * 20}px ${c}`,
      ].join(', ');
    }
    case 'emboss': {
      const d = Math.max(1, Math.round(i * 0.5));
      const dark  = _hexRgba(c, 0.3 + i * 0.06);
      const light = `rgba(255,255,255,${(0.1 + i * 0.02).toFixed(2)})`;
      return `${d}px ${d}px ${d * 2}px ${dark}, -${d}px -${d}px ${d}px ${light}`;
    }
  }
}

// ============================================================================

export interface SlideStyle {
  bgColor: string;
  /** Mutually exclusive with bgPattern. Set bgImage and clear bgPattern (or vice-versa). */
  bgImage?: string | null;
  /** CSS-rendered background. Mutually exclusive with bgImage. */
  bgPattern?: BgPattern | null;
  /** Semi-transparent color overlay on top of bgImage/bgPattern for contrast boost. */
  bgOverlayColor?: string;
  /** Opacity of bgOverlayColor (0–1). Default 0. */
  bgOverlayOpacity?: number;
  fontColor: string;
  fontSize: string;
  fontFamily: string;
  fontWeight?: number;
  fontStyle?: 'normal' | 'italic';
  textAlign: 'left' | 'center' | 'right';
  /** CSS line-height (unitless multiplier). Default 1.4. Range 0.9–2.5. */
  lineHeight?: number;
  /** CSS letter-spacing in em units. Default 0. Range -0.05–0.3. */
  letterSpacing?: number;
  /** Text shadow / glow / outline effect. null = none. */
  textEffect?: TextEffect | null;
  /** CSS text-transform. undefined/'none' = as-typed. */
  textTransform?: 'none' | 'uppercase' | 'lowercase';
  transition: StyleTransition;
  /** Override transition duration in ms (0-1000). Defaults per preset:
   *  cut=0, fade=200, slide-up=300, zoom=400. */
  transitionDurationMs?: number;
  /** Beat pause in ms between image-fully-visible and text fade-in start (0-500).
   *  Defaults: cut=0, fade=80, slide-up=100, zoom=120. */
  transitionTextDelayMs?: number;
  /** Canvas Designer mode active — renderer uses content.elements[] instead of mainText/subText. */
  canvasMode?: boolean;
}

/** Sensible default duration + text delay per transition preset. */
export const TRANSITION_DEFAULTS: Record<TransitionKind, { durMs: number; textDelayMs: number }> = {
  cut:        { durMs: 0,   textDelayMs: 0   },
  fade:       { durMs: 200, textDelayMs: 80  },
  'slide-up': { durMs: 300, textDelayMs: 100 },
  zoom:       { durMs: 400, textDelayMs: 120 },
};

/** Normalize legacy enum values to canonical TransitionKind. */
export function normalizeTransition(t: StyleTransition): TransitionKind {
  if (t === 'none') return 'cut';
  if (t === 'slide') return 'slide-up';
  return t;
}

/** Resolve the effective transition kind + durations for a slide. Caller-supplied
 *  values override the preset defaults; missing values fall back per preset. */
export function resolveTransition(style: Pick<SlideStyle, 'transition' | 'transitionDurationMs' | 'transitionTextDelayMs'>): {
  kind: TransitionKind;
  durMs: number;
  textDelayMs: number;
} {
  const kind = normalizeTransition(style.transition);
  const defaults = TRANSITION_DEFAULTS[kind];
  return {
    kind,
    durMs: style.transitionDurationMs ?? defaults.durMs,
    textDelayMs: style.transitionTextDelayMs ?? defaults.textDelayMs,
  };
}

export interface SlideGroup {
  id: string;
  orgId: string;
  presentationId: string;
  label: string;
  orderNum: number;
  updatedAt: string;
}

export interface SlideSection {
  id: string;
  orgId: string;
  groupId: string;
  label: string;
  orderNum: number;
  updatedAt: string;
}

export interface Slide {
  id: string;
  presentationId: string;
  orderNum: number;
  type: SlideType;
  content: SlideContent;
  style: SlideStyle;
  groupId?: string | null;
  /** Read-only: populated by JOIN with slide_groups, not stored in slides table. */
  groupLabel?: string | null;
  sectionId?: string | null;
  /** Read-only: populated by JOIN with slide_sections, not stored in slides table. */
  sectionLabel?: string | null;
}

// === Presentation Types ===

export interface Presentation {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  category?: string;
}

export interface PresentationWithSlides extends Presentation {
  slides: Slide[];
  groups: SlideGroup[];
}

// === Bible Types ===

export type Testament = 'AT' | 'NT';
export type BibleVersion = 'RVR1960' | 'NTV';
export const BIBLE_VERSIONS: BibleVersion[] = ['RVR1960', 'NTV'];

export interface BibleBook {
  id: number;
  name: string;
  testament: Testament;
  orderNum: number;
}

export interface BibleVerse {
  id: number;
  bookId: number;
  chapter: number;
  verse: number;
  text: string;
  version: BibleVersion;
}

export interface BibleVerseWithBook extends BibleVerse {
  bookName: string;
}

// === Song Types ===

export type SongSectionType = 'verse' | 'chorus' | 'bridge' | 'pre_chorus' | 'intro' | 'outro';

export interface Song {
  id: string;
  title: string;
  author?: string;
  category?: string;
  tags?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SongSection {
  id: string;
  songId: string;
  type: SongSectionType;
  label: string;
  orderNum: number;
  lyrics: string;
}

export interface SongArrangement {
  id: string;
  songId: string;
  name: string;
  sectionOrder: string[]; // array of section IDs
}

export interface SongWithSections extends Song {
  sections: SongSection[];
  arrangements: SongArrangement[];
}

// === Font Types ===

export interface CustomFontFile {
  id: string;
  fontId: string;
  filename: string;
  weight: number;              // 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900
  style: 'normal' | 'italic';
  variable: boolean;           // true → font-weight: 100 900 (variable font)
  publicUrl?: string | null;   // CDN/S3 public URL — null when cloud storage not configured
}

export interface CustomFont {
  id: string;
  label: string;     // display name: "Mi Fuente Especial"
  cssFamily: string; // CSS font-family value (same as label)
  files: CustomFontFile[];
  createdAt: string;
}

// === Image Types ===

export interface UploadedImage {
  id: string;           // SHA-256 of compressed content (CAS key)
  filename: string;
  url: string;          // local absolute URL (http://host:port/uploads/images/…)
  cloudUrl: string | null; // R2/CDN HTTPS URL — used by Cast receiver
  originalName: string | null;
  size: number | null;  // bytes after compression
  createdAt: number;    // Unix ms
}

// === Tenant Types ===

export type OrgPlan = 'free' | 'pro';
export type OrgRole = 'admin' | 'editor';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  plan: OrgPlan;
  createdAt: string;
}

export interface User {
  id: string;
  clerkId: string;
  email: string;
  name?: string | null;
  createdAt: string;
}

export interface OrgMember {
  id: string;
  orgId: string;
  userId: string;
  role: OrgRole;
  joinedAt: string;
}

// === Sync Types ===

export type SyncTable =
  | 'presentations'
  | 'slides'
  | 'slide_groups'
  | 'slide_sections'
  | 'songs'
  | 'song_sections'
  | 'song_arrangements'
  | 'custom_fonts'
  | 'custom_font_files'
  | 'uploaded_images';

export type SyncOperation = 'upsert' | 'delete';

export interface SyncOp {
  table: SyncTable;
  recordId: string;
  operation: SyncOperation;
  payload: Record<string, unknown>;
  clientTimestamp: number; // Unix ms
}

export interface SyncPushResponse {
  accepted: string[]; // recordIds accepted
  conflicts: Array<{ recordId: string; serverVersion: number }>; // LWW: server won
}

export interface SyncPullResponse {
  since: number;
  ops: SyncOp[];
}

// === Cast Types ===

export type VideoAction = 'play' | 'pause' | 'stop' | 'reset' | 'mute' | 'unmute' | 'seek';

export interface CastMessage {
  type: 'SHOW_SLIDE' | 'BLACK_SCREEN' | 'CLEAR' | 'PING' | 'PRELOAD_IMAGES' | 'RECEIVER_COUNT' | 'HELLO' | 'SESSION_INFO' | 'STOP_CAST' | 'DEVICES_INFO' | 'START_CAST' | 'VIDEO_CONTROL' | 'VIDEO_REQUEST_SYNC' | 'VIDEO_DURATION' | 'VIDEO_TICK' | 'VIDEO_LOOPED' | 'VIDEO_PLAYHEAD' | 'RECEIVER_LOG' | 'RECEIVER_READY';
  /**
   * Remote logging from the receiver (RECEIVER_LOG). The Chromecast has no
   * reachable console, so the receiver buffers errors + lifecycle events and
   * ships them upstream (Cast custom message / WS) to be read on the sender and
   * persisted in the Electron diag report.
   */
  logLevel?: 'error' | 'warn' | 'info';
  logMessage?: string;
  /** Receiver wall-clock (Date.now()) when the event happened. */
  logTs?: number;
  /** Origin tag: 'window.onerror' | 'unhandledrejection' | 'keepalive' | 'watchdog' | 'wakelock' | 'cast' | 'ws'. */
  logSource?: string;
  /** Monotonic per-receiver sequence — lets the consumer dedup re-flushed buffers. */
  logSeq?: number;
  videoAction?: VideoAction;
  /** Seek target in seconds. Used with videoAction === 'seek' for multi-receiver sync. */
  seekTime?: number;
  /**
   * Client wall-clock (Date.now()) attached by senders/receivers when requesting
   * a sync. Server echoes it back in the response so the requester can estimate
   * RTT and compensate the seek target.
   */
  clientNow?: number;
  /**
   * Server wall-clock (Date.now()) attached by the server to any VIDEO_CONTROL
   * computed from the master clock (cold-join, REQUEST_SYNC, drift correction).
   * Receivers use it together with echoClientNow to compute the network offset.
   */
  serverNow?: number;
  /**
   * Echo of the requester's clientNow in the matching response. Lets the
   * requester compute one-way latency without needing a synced clock.
   */
  echoClientNow?: number;
  /**
   * Video duration in seconds. Sent by receiver (or sender's local-audio iframe)
   * once metadata is known, so the server can apply modulo on its master clock.
   */
  videoDuration?: number;
  /**
   * Expected playhead in seconds, sampled at serverNow. Periodic VIDEO_TICK
   * messages let receivers detect and correct drift via micro-seek or
   * playbackRate nudges without disrupting the viewer.
   */
  expectedSeek?: number;
  /**
   * Seek target to bake into the initial render of a cold-joining receiver.
   * Server piggy-backs this on the cached SHOW_SLIDE so a YouTube iframe can
   * load with &start=N directly (avoids the post-ready seekTo round-trip).
   */
  initialSeekTime?: number;
  /**
   * Real playhead reported upstream by a receiver. Server uses this as the
   * authoritative source for the master clock when fresh, replacing the
   * theoretical wall-clock estimate.
   */
  actualSec?: number;
  /**
   * Kind of receiver — informs buffer-aware compensation server-side.
   * 'ws' = browser WebSocket receiver (minimal buffer).
   * 'cast' / 'caf' = Cast SDK receiver running on Chromecast device.
   */
  deviceKind?: 'ws' | 'cast' | 'caf';
  /** CSS @font-face rules for custom fonts (public URLs). Receiver injects into <style> tag. */
  fontFaceCSS?: string;
  /** Map from font family UUID to CSS font-family string. Receiver merges into its fontFamilyMap. */
  fontFamilyMap?: Record<string, string>;
  slide?: SlideContent & { style: SlideStyle };
  /** Image URLs for the receiver to pre-cache before slides are shown. */
  imageUrls?: string[];
  /**
   * Unix ms — receiver delays rendering until this time.
   * Set by the sender when broadcasting to multiple Cast sessions so all devices
   * switch simultaneously despite different network RTTs.
   */
  presentAt?: number;
  /** @deprecated use senderCount + receiverCount */
  count?: number;
  /** Role announced by the client on connect. */
  role?: 'sender' | 'receiver';
  /** Active Chromecast device names — broadcast by the desktop sender when session changes. */
  castDevices?: string[];
  /** Discovered (available) Chromecast device names — broadcast by the desktop sender. */
  devices?: string[];
  /** Target device name for START_CAST. */
  deviceName?: string;
  /** Number of sender (controller) connections, including self. */
  senderCount?: number;
  /** Number of receiver (display) connections. */
  receiverCount?: number;
  /** Slide UUID — included by senders so secondary controllers can sync currentSlideIndex. Ignored by receivers. */
  slideId?: string;
  /** True when the slide is a quick-cast verse, not a deck slide. Secondary controllers show the quick-verse banner. */
  isQuickSlide?: boolean;
  /** Presentation UUID — lets secondary controllers detect deck changes and re-fetch slides. Ignored by receivers. */
  presentationId?: string;
  /** Total slides in sender's deck — secondary controllers compare to detect additions/deletions. Ignored by receivers. */
  slideCount?: number;
}

// === API Types ===

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// === Result Pattern ===

export type Result<T, E = string> =
  | { ok: true; value: T }
  | { ok: false; error: E };

export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

// === Default Styles ===

export const DEFAULT_SLIDE_STYLE: SlideStyle = {
  bgColor: '#000000',
  bgImage: null,
  fontColor: '#FFFFFF',
  fontSize: '4xl',
  fontFamily: 'inter',
  fontWeight: 600,
  fontStyle: 'normal',
  textAlign: 'center',
  lineHeight: 1.4,
  letterSpacing: 0,
  transition: 'fade',
};

export const CAST_NAMESPACE = 'urn:x-cast:com.slidecast.presentation';
